import type { ApiPromise } from '@polkadot/api';
import type { Vec, Struct, Option } from '@polkadot/types/codec';
import type { u32, u64, u128 } from '@polkadot/types/primitive';
import type { AccountId32, Balance } from '@polkadot/types/interfaces';
import type { Signature } from '@polkadot/types/interfaces/extrinsics';
import type { Signer } from '@polkadot/api/types';
import type { Path } from 'ramda';

import * as R from 'ramda';

import {
  CareerEnum,
  LevelEnum,
  RaceEnum,
  CAREER_CHAIN_MAP,
  LEVEL_CHAIN_MAP,
  RACE_CHAIN_MAP,
  CAREER_TYPE_CHAIN_MAP,
  LEVEL_TYPE_CHAIN_MAP,
  RACE_TYPE_CHAIN_MAP
} from '@/types/nftAttribute';
import { waitSignAndSend } from '@/utils/signAndSend';

export type PreorderId = u32 | number;
export type EraId = u64 | number;
export type GenerationId = u32 | number;
export type CollectionId = u32 | number;
export type NftId = u32 | number;

export interface FrameSystemAccountInfo extends Struct {
  readonly nonce: u32;
  readonly consumers: u32;
  readonly providers: u32;
  readonly sufficients: u32;
  readonly data: PalletBalancesAccountData;
}

export interface PalletBalancesAccountData extends Struct {
  readonly free: Balance;
  readonly reserved: u128;
  readonly miscFrozen: u128;
  readonly feeFrozen: u128;
}

export type RarityType = string | 'Prime' | 'Magic' | 'Legendary';
export type RaceType = string | 'Cyborg' | 'AISpectre' | 'XGene' | 'Pandroid';
export type CareerType = string | CareerEnum;

type SignatureLike = Signature | string | Uint8Array;

export interface NumberOption extends Option<u32> {
  toJSON: () => number;
}

export type OriginOfShell = {
  account: AccountId32 | string;
  collectionId: number;
  nftId: number;
  career: CareerEnum;
  generation: number;
  race: RaceType;
  rarity: RarityType;
};

export type NftSaleInfo = {
  raceCount: number;
  raceForSaleCount: number;
  raceGiveawayCount: number;
  raceReservedCount: number;
};

export interface NftSaleInfoStruct extends Struct {
  raceCount: u32;
  raceForSaleCount: u32;
  raceGiveawayCount: u32;
  raceReservedCount: u32;
  toHuman(): NftSaleInfo;
}

export type PreorderInfo = {
  owner: string;
  career: string;
  race: RaceType;
  metadata: string;
};

export interface PreorderInfoStruct extends Struct {
  owner: AccountId32;
  career: string;
  race: RaceType;
  metadata: string;
  toHuman(): PreorderInfo;
}

export interface NftMetadataStruct extends Struct {
  owner: {
    AccountId: AccountId32 | string;
  };
}

export type FoodInfo = {
  era: u64;
  // @Fixme: FoodInfo<BoundedVec<(CollectionId, NftId), <T as Config>::FoodPerEra>>
  originOfShellsFed: Vec<any>;
};

//
// END: Custom queries and tx.
//

//
// Wrap the api with transforms.
//

export async function getNftSaleState(api: ApiPromise) {
  const result = await api.queryMulti([
    api.query.pwNftSale.canPreorderOriginOfShells,
    api.query.pwNftSale.canPurchaseRareOriginOfShells,
    api.query.pwNftSale.canPurchasePrimeOriginOfShells,
    api.query.pwNftSale.lastDayOfSale
  ]);
  const inventoryQuery = await Promise.all([
    api.query.pwNftSale.originOfShellsInventory.entries('Prime'),
    api.query.pwNftSale.originOfShellsInventory.entries('Magic'),
    api.query.pwNftSale.originOfShellsInventory.entries('Legendary')
  ]);

  const allBooleans = result.map((i) => i?.toHuman() || false);
  const [canPreorderOriginOfShells, canPurchaseRareOriginOfShells, canPurchasePrimeOriginOfShells, lastDayOfSale] =
    allBooleans as boolean[];

  const inventoryLevel = R.mergeAll(
    R.map(
      R.reduce(
        (acc, i) => R.assocPath(i[0]?.toHuman() as Path, i[1]?.toJSON(), acc),
        {} as Record<RarityType, Record<RaceType, NftSaleInfo>>
      ),
      inventoryQuery
    )
  );

  return {
    canPreorderOriginOfShells: !!canPreorderOriginOfShells,
    canPurchaseRareOriginOfShells: !!canPurchaseRareOriginOfShells,
    canPurchasePrimeOriginOfShells: !!canPurchasePrimeOriginOfShells,
    lastDayOfSale: !!lastDayOfSale,
    inventoryLevel
  };
}

export async function getOriginOfShellCollectionId(api: ApiPromise) {
  const raw = await api.query.pwNftSale.originOfShellCollectionId();
  return raw?.toJSON();
}

export async function getSpiritCollectionId(api: ApiPromise) {
  const raw = await api.query.pwNftSale.spiritCollectionId();
  return raw?.toJSON();
}

export async function getShellCollectionId(api: ApiPromise) {
  const raw = await api.query.pwIncubation.shellCollectionId();
  return raw?.toJSON();
}

export async function getCrucialCollectionIds(api: ApiPromise) {
  const collectionIds: NumberOption[] = await api.queryMulti([
    api.query.pwNftSale.originOfShellCollectionId,
    api.query.pwNftSale.spiritCollectionId,
    api.query.pwIncubation.shellCollectionId
  ]);
  const [originOfShellCollectionId, spiritCollectionId, shellCollectionId] = collectionIds.map((i) =>
    i ? i.toJSON() : 0
  );
  return {
    originOfShellCollectionId: originOfShellCollectionId as number,
    spiritCollectionId: spiritCollectionId as number,
    shellCollectionId: shellCollectionId as number
  };
}

export async function getOriginOfShellCollectionForAccountAndId(
  api: ApiPromise,
  account: string | AccountId32,
  collectionId: number | u32
): Promise<OriginOfShell[]> {
  const uniques = await api.query.uniques.account.entries(account as AccountId32, collectionId as u32);
  const records = uniques.map(([key]) => {
    const [, rawCollectionId, rawNftId] = key.args;
    const collectionId = rawCollectionId.toJSON();
    const nftId = rawNftId.toJSON();
    return [collectionId, nftId] as [number, number];
  });

  const queries = R.map(([collectionId, nftId]) => api.query.uniques.attribute.entries(collectionId, nftId), records);
  const pairsQuery = await Promise.all(queries);
  const nftAttrs = R.map(
    (lst) =>
      R.fromPairs(
        R.map(([k, v]): [string, string | number | null | undefined] => {
          const key = k?.args[2]?.toHuman();
          const val = v?.unwrap()[0].toHuman();
          if (key === 'race') {
            return [key, RACE_TYPE_CHAIN_MAP[api.createType('RaceType', val).toString()]];
          } else if (key === 'career') {
            return [key, CAREER_TYPE_CHAIN_MAP[api.createType('CareerType', val).toString()]];
          } else if (key === 'rarity') {
            return [key, LEVEL_TYPE_CHAIN_MAP[api.createType('RarityType', val).toString()]];
          } else if (key === 'generation') {
            return [key, parseInt(val, 16)];
          } else {
            return [key as string, null];
          }
        }, lst)
      ) as Omit<OriginOfShell, 'collectionId' | 'nftId' | 'account'>,
    pairsQuery
  );
  return R.map(
    (pairs: [[number, number], Omit<OriginOfShell, 'collectionId' | 'nftId' | 'account'>]): OriginOfShell => {
      const [[collectionId, nftId], attrs] = pairs;
      return {
        account,
        collectionId,
        nftId,
        ...attrs
      };
    },
    R.zip(records, nftAttrs)
  );
}

export async function getSigner(account: string | AccountId32) {
  const { web3FromAddress } = await import('@polkadot/extension-dapp');
  return (await web3FromAddress(account as string)).signer;
}

/**
 * Only Prime rarity is supported.
 */
export async function preOrderOriginOfShell(
  api: ApiPromise,
  signer: Signer,
  account: string | AccountId32,
  race: RaceEnum,
  career: CareerEnum
) {
  const extrinsic = api.tx.pwNftSale.preorderOriginOfShell(RACE_CHAIN_MAP[race], CAREER_CHAIN_MAP[career]);
  if (extrinsic) {
    try {
      await waitSignAndSend({
        account,
        api,
        extrinsic,
        signer
      });
      return true;
    } catch (err) {
      throw R.is(Array, err) ? err[0] : err;
    }
  }
  return false;
}

export async function buyRareOriginOfShell(
  api: ApiPromise,
  signer: Signer,
  account: string | AccountId32,
  race: RaceEnum,
  career: CareerEnum,
  rarity: LevelEnum
) {
  const extrinsic = api.tx.pwNftSale.buyRareOriginOfShell(
    LEVEL_CHAIN_MAP[rarity],
    RACE_CHAIN_MAP[race],
    CAREER_CHAIN_MAP[career]
  );
  if (extrinsic) {
    try {
      await waitSignAndSend({
        account,
        api,
        extrinsic,
        signer
      });
      return true;
    } catch (err) {
      throw R.is(Array, err) ? err[0] : err;
    }
  }
  return false;
}

export async function buyPrimeOriginOfShell(
  api: ApiPromise,
  signer: Signer,
  account: string | AccountId32,
  race: RaceEnum,
  career: CareerEnum,
  whitelistTicketSignature = ''
) {
  const extrinsic = api.tx.pwNftSale.buyPrimeOriginOfShell(
    whitelistTicketSignature,
    RACE_CHAIN_MAP[race],
    CAREER_CHAIN_MAP[career]
  );
  if (extrinsic) {
    try {
      await waitSignAndSend({
        account,
        api,
        extrinsic,
        signer
      });
      return true;
    } catch (err) {
      throw R.is(Array, err) ? err[0] : err;
    }
  }
  return false;
}
